import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Sparkles, Heart, Zap, Star } from 'lucide-react';
// Types
type QuizSection = 'intro' | 'questions' | 'loading' | 'results' | 'offer';
type BellyType = 'TYPE1' | 'TYPE2' | 'TYPE3' | 'TYPE4';
type PrimaryGoal = 'BELLY' | 'ENERGY' | 'SKIN' | 'IDENTITY';
interface QuizState {
  currentSection: QuizSection;
  currentQuestion: number;
  answers: Record<number, string | string[]>;
  points: {
    TYPE1: number;
    TYPE2: number;
    TYPE3: number;
    TYPE4: number;
  };
  primaryGoal: PrimaryGoal | null;
  resultType: BellyType | null;
}
const QUESTIONS = [{
  id: 1,
  progress: 7,
  emoji: '🎂',
  question: 'How old are you?',
  type: 'single' as const,
  options: [{
    value: 'under45',
    label: 'Under 45',
    emoji: '🌱'
  }, {
    value: '45-49',
    label: '45-49',
    emoji: '🌸'
  }, {
    value: '50-54',
    label: '50-54',
    emoji: '🌺'
  }, {
    value: '55-59',
    label: '55-59',
    emoji: '🌻'
  }, {
    value: '60plus',
    label: '60+',
    emoji: '🌹'
  }]
}, {
  id: 2,
  progress: 14,
  emoji: '🌊',
  question: 'Your menopause journey?',
  type: 'single' as const,
  options: [{
    value: 'peri',
    label: 'Perimenopause',
    emoji: '🌊',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'meno',
    label: 'Menopause',
    emoji: '🔥'
  }, {
    value: 'post',
    label: 'Post-Menopause',
    emoji: '🌿',
    points: {
      TYPE4: 1
    }
  }, {
    value: 'unsure',
    label: 'Not Sure',
    emoji: '🤷'
  }]
}, {
  id: 3,
  progress: 21,
  emoji: '🪞',
  question: 'What bothers you most?',
  type: 'single' as const,
  options: [{
    value: 'belly',
    label: 'Belly',
    emoji: '👗',
    primaryGoal: 'BELLY' as PrimaryGoal
  }, {
    value: 'energy',
    label: 'Energy',
    emoji: '⚡',
    primaryGoal: 'ENERGY' as PrimaryGoal
  }, {
    value: 'skin',
    label: 'Skin',
    emoji: '✨',
    primaryGoal: 'SKIN' as PrimaryGoal
  }, {
    value: 'identity',
    label: 'Feel Like Me',
    emoji: '🧘',
    primaryGoal: 'IDENTITY' as PrimaryGoal
  }]
}, {
  id: 4,
  progress: 28,
  emoji: '⏰',
  question: 'When is bloating worst?',
  type: 'single' as const,
  options: [{
    value: 'morning',
    label: 'Morning',
    emoji: '🌅'
  }, {
    value: 'midday',
    label: 'Midday',
    emoji: '☀️'
  }, {
    value: '4pm',
    label: 'Afternoon',
    emoji: '🕓',
    points: {
      TYPE1: 2
    }
  }, {
    value: 'night',
    label: 'Night',
    emoji: '🌙'
  }, {
    value: 'allday',
    label: 'All Day',
    emoji: '⏰',
    points: {
      TYPE2: 2
    }
  }]
}, {
  id: 5,
  progress: 35,
  emoji: '🏃',
  question: 'What have you tried?',
  subtext: 'Pick all that apply',
  type: 'multi' as const,
  options: [{
    value: 'cardio',
    label: 'Cardio',
    emoji: '🏃'
  }, {
    value: 'diet',
    label: 'Dieting',
    emoji: '🥗'
  }, {
    value: 'supps',
    label: 'Supplements',
    emoji: '💊'
  }, {
    value: 'water',
    label: 'More Water',
    emoji: '💧'
  }, {
    value: 'elimination',
    label: 'Cut Foods',
    emoji: '🚫'
  }, {
    value: 'nothing',
    label: 'Nothing Yet',
    emoji: '😔'
  }]
}, {
  id: 6,
  progress: 42,
  emoji: '⚡',
  question: 'Your energy levels?',
  type: 'single' as const,
  options: [{
    value: 'exhausted',
    label: 'Exhausted',
    emoji: '😴'
  }, {
    value: 'crashes',
    label: 'PM Crashes',
    emoji: '📉',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'inconsistent',
    label: 'Inconsistent',
    emoji: '🎢'
  }, {
    value: 'okay',
    label: 'Okay-ish',
    emoji: '😐'
  }]
}, {
  id: 7,
  progress: 49,
  emoji: '😰',
  question: 'Bloat more when stressed?',
  type: 'single' as const,
  options: [{
    value: 'yes-absolutely',
    label: 'Yes!',
    emoji: '✅',
    points: {
      TYPE1: 2
    }
  }, {
    value: 'yes-thinking',
    label: 'Now that you mention it...',
    emoji: '🤔',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'not-sure',
    label: 'Not Sure',
    emoji: '❓',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'not-really',
    label: 'Not Really',
    emoji: '❌',
    points: {
      TYPE2: 1
    }
  }]
}, {
  id: 8,
  progress: 56,
  emoji: '🍽️',
  question: 'Eating less helped?',
  type: 'single' as const,
  options: [{
    value: 'worse',
    label: 'Made it Worse',
    emoji: '😩',
    points: {
      TYPE4: 2
    }
  }, {
    value: 'no-change',
    label: 'No Change',
    emoji: '😐',
    points: {
      TYPE4: 1
    }
  }, {
    value: 'havent-tried',
    label: "Haven't Tried",
    emoji: '🤷'
  }, {
    value: 'used-to-work',
    label: 'Used to Work',
    emoji: '✅',
    points: {
      TYPE4: 1
    }
  }]
}, {
  id: 9,
  progress: 60,
  emoji: '🏋️',
  question: 'How often intense exercise?',
  type: 'single' as const,
  options: [{
    value: 'every-day',
    label: 'Every Day',
    emoji: '🏋️',
    points: {
      TYPE1: 2
    }
  }, {
    value: 'few-times',
    label: 'Few Times/Week',
    emoji: '🚴',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'light',
    label: 'Light Movement',
    emoji: '🚶',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'little',
    label: 'Little/None',
    emoji: '😔',
    points: {
      TYPE4: 1
    }
  }]
}, {
  id: 10,
  progress: 67,
  emoji: '🤔',
  question: 'Mystery symptoms?',
  subtext: 'Pick all that apply',
  type: 'multi' as const,
  options: [{
    value: 'puffy-face',
    label: 'Puffy Face',
    emoji: '😶',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'swollen-ankles',
    label: 'Swollen Ankles',
    emoji: '👣',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'brain-fog',
    label: 'Brain Fog',
    emoji: '🧠',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'poor-sleep',
    label: 'Poor Sleep',
    emoji: '😴',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'inflamed',
    label: 'Achy/Inflamed',
    emoji: '🌡️',
    points: {
      TYPE2: 2
    }
  }, {
    value: 'all',
    label: 'All of These!',
    emoji: '✅',
    points: {
      TYPE2: 2,
      TYPE3: 2
    }
  }]
}, {
  id: 11,
  progress: 74,
  emoji: '🤰',
  question: 'Flat morning → pregnant evening?',
  type: 'single' as const,
  options: [{
    value: 'yes-every-day',
    label: 'Every Day!',
    emoji: '😩',
    points: {
      TYPE1: 2
    }
  }, {
    value: 'sometimes',
    label: 'Sometimes',
    emoji: '😐',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'occasionally',
    label: 'Occasionally',
    emoji: '🤔',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'not-really',
    label: 'Not Really',
    emoji: '❌',
    points: {
      TYPE2: 1
    }
  }]
}, {
  id: 12,
  progress: 81,
  emoji: '💪',
  question: 'How committed are you?',
  type: 'single' as const,
  options: [{
    value: 'committed',
    label: '100%!',
    emoji: '💪'
  }, {
    value: 'pretty-committed',
    label: 'Pretty Committed',
    emoji: '👍'
  }, {
    value: 'cautious',
    label: 'Cautiously Optimistic',
    emoji: '🤞'
  }, {
    value: 'desperate',
    label: 'Desperate!',
    emoji: '🙏'
  }]
}, {
  id: 13,
  progress: 88,
  emoji: '🎯',
  question: 'What matters most?',
  type: 'single' as const,
  options: [{
    value: 'belly',
    label: 'Flat Belly',
    emoji: '👗',
    primaryGoal: 'BELLY' as PrimaryGoal
  }, {
    value: 'energy',
    label: 'More Energy',
    emoji: '⚡',
    primaryGoal: 'ENERGY' as PrimaryGoal
  }, {
    value: 'skin',
    label: 'Firm Skin',
    emoji: '✨',
    primaryGoal: 'SKIN' as PrimaryGoal
  }, {
    value: 'identity',
    label: 'Feel Like Me',
    emoji: '🧘',
    primaryGoal: 'IDENTITY' as PrimaryGoal
  }]
}, {
  id: 14,
  progress: 95,
  emoji: '🔍',
  question: "Why hasn't it worked yet?",
  type: 'single' as const,
  options: [{
    value: 'didnt-know',
    label: "Didn't Know Why",
    emoji: '🔍'
  }, {
    value: 'tried-everything',
    label: 'Tried Everything',
    emoji: '😩'
  }, {
    value: 'no-time',
    label: 'No Time',
    emoji: '⏰'
  }, {
    value: 'wasted-money',
    label: 'Wasted Money',
    emoji: '💸'
  }]
}];
export function AnimatedQuizFunnel() {
  const [state, setState] = useState<QuizState>({
    currentSection: 'intro',
    currentQuestion: 1,
    answers: {},
    points: {
      TYPE1: 0,
      TYPE2: 0,
      TYPE3: 0,
      TYPE4: 0
    },
    primaryGoal: null,
    resultType: null
  });
  const handleAnswer = useCallback((questionId: number, answer: string | string[]) => {
    setState(prev => {
      const question = QUESTIONS.find(q => q.id === questionId);
      let newPoints = {
        ...prev.points
      };
      let newPrimaryGoal = prev.primaryGoal;
      if (question) {
        if (question.type === 'single') {
          const option = question.options.find(o => o.value === answer);
          if (option) {
            if ('points' in option && option.points) {
              Object.entries(option.points).forEach(([type, points]) => {
                newPoints[type as keyof typeof newPoints] += points;
              });
            }
            if ('primaryGoal' in option && option.primaryGoal) {
              newPrimaryGoal = option.primaryGoal;
            }
          }
        } else if (question.type === 'multi' && Array.isArray(answer)) {
          answer.forEach(val => {
            const option = question.options.find(o => o.value === val);
            if (option && 'points' in option && option.points) {
              Object.entries(option.points).forEach(([type, points]) => {
                newPoints[type as keyof typeof newPoints] += points;
              });
            }
          });
        }
      }
      const isLastQuestion = questionId === 14;
      if (question?.type === 'single') {
        if (isLastQuestion) {
          const resultType = Object.entries(newPoints).reduce((a, b) => b[1] > a[1] ? b : a)[0] as BellyType;
          return {
            ...prev,
            answers: {
              ...prev.answers,
              [questionId]: answer
            },
            points: newPoints,
            primaryGoal: newPrimaryGoal,
            resultType,
            currentSection: 'loading'
          };
        } else {
          return {
            ...prev,
            answers: {
              ...prev.answers,
              [questionId]: answer
            },
            points: newPoints,
            primaryGoal: newPrimaryGoal,
            currentQuestion: questionId + 1
          };
        }
      }
      return {
        ...prev,
        answers: {
          ...prev.answers,
          [questionId]: answer
        },
        points: newPoints,
        primaryGoal: newPrimaryGoal
      };
    });
  }, []);
  const handleMultiContinue = useCallback(() => {
    setState(prev => {
      if (prev.currentQuestion === 14) {
        const resultType = Object.entries(prev.points).reduce((a, b) => b[1] > a[1] ? b : a)[0] as BellyType;
        return {
          ...prev,
          resultType,
          currentSection: 'loading'
        };
      }
      return {
        ...prev,
        currentQuestion: prev.currentQuestion + 1
      };
    });
  }, []);
  useEffect(() => {
    if (state.currentSection === 'loading') {
      const timer = setTimeout(() => {
        setState(prev => ({
          ...prev,
          currentSection: 'results'
        }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [state.currentSection]);
  return <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50" data-id="element-389">
      {state.currentSection === 'intro' && <IntroSection onStart={() => setState(prev => ({
      ...prev,
      currentSection: 'questions'
    }))} data-id="element-390" />}
      {state.currentSection === 'questions' && <QuestionsSection currentQuestion={state.currentQuestion} answers={state.answers} onAnswer={handleAnswer} onMultiContinue={handleMultiContinue} data-id="element-391" />}
      {state.currentSection === 'loading' && <LoadingSection data-id="element-392" />}
      {state.currentSection === 'results' && <ResultsSection resultType={state.resultType!} primaryGoal={state.primaryGoal!} onContinue={() => setState(prev => ({
      ...prev,
      currentSection: 'offer'
    }))} data-id="element-393" />}
      {state.currentSection === 'offer' && <OfferSection data-id="element-394" />}
    </div>;
}
function IntroSection({
  onStart
}: {
  onStart: () => void;
}) {
  return <div className="min-h-screen flex items-center justify-center px-4 py-8" data-id="element-395">
      <div className="w-full max-w-md text-center animate-fade-in" data-id="element-396">
        {/* Animated emoji */}
        <div className="text-8xl mb-6 animate-bounce" data-id="element-397">🌸</div>

        <h1 className="text-4xl font-bold text-purple-900 mb-4 leading-tight" data-id="element-398">
          Your Belly Quiz
        </h1>

        <p className="text-xl text-purple-700 mb-8" data-id="element-399">
          Find your belly type in 60 seconds!
        </p>

        <div className="bg-white/80 backdrop-blur rounded-3xl p-6 mb-8 shadow-xl" data-id="element-400">
          <div className="flex justify-center gap-1 mb-3" data-id="element-401">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-5 h-5 text-amber-400 fill-current" data-id="element-402" />)}
          </div>
          <p className="text-sm text-purple-600 font-medium" data-id="element-403">
            14,847 women completed this quiz
          </p>
        </div>

        <button onClick={onStart} className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-6 px-8 rounded-full shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 text-xl" data-id="element-404">
          <Sparkles className="w-6 h-6" data-id="element-405" />
          Start Quiz
          <ArrowRight className="w-6 h-6" data-id="element-406" />
        </button>

        <p className="text-xs text-purple-500 mt-4" data-id="element-407">
          🔒 Free • Private • 60 seconds
        </p>
      </div>
    </div>;
}
function QuestionsSection({
  currentQuestion,
  answers,
  onAnswer,
  onMultiContinue
}: {
  currentQuestion: number;
  answers: Record<number, string | string[]>;
  onAnswer: (questionId: number, answer: string | string[]) => void;
  onMultiContinue: () => void;
}) {
  const question = QUESTIONS.find(q => q.id === currentQuestion);
  if (!question) return null;
  const currentAnswer = answers[currentQuestion];
  const isMulti = question.type === 'multi';
  const hasAnswer = isMulti ? Array.isArray(currentAnswer) && currentAnswer.length > 0 : !!currentAnswer;
  return <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8" data-id="element-408">
      <div className="w-full max-w-md" data-id="element-409">
        {/* Progress */}
        <div className="mb-8" data-id="element-410">
          <div className="flex justify-between items-center mb-2" data-id="element-411">
            <span className="text-sm font-bold text-purple-600" data-id="element-412">
              Q{currentQuestion}/14
            </span>
            <span className="text-sm font-bold text-pink-600" data-id="element-413">
              {question.progress}%
            </span>
          </div>
          <div className="h-3 bg-white/50 rounded-full overflow-hidden" data-id="element-414">
            <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 transition-all duration-500 rounded-full" style={{
            width: `${question.progress}%`
          }} data-id="element-415" />
          </div>
        </div>

        {/* Question with animated emoji */}
        <div className="text-center mb-8 animate-slide-up" data-id="element-416">
          <div className="text-7xl mb-4 animate-bounce-slow" data-id="element-417">
            {question.emoji}
          </div>
          <h2 className="text-2xl font-bold text-purple-900 mb-2" data-id="element-418">
            {question.question}
          </h2>
          {question.subtext && <p className="text-sm text-purple-600" data-id="element-419">{question.subtext}</p>}
        </div>

        {/* Options */}
        <div className="space-y-3 mb-6" data-id="element-420">
          {question.options.map((option, idx) => {
          const isSelected = isMulti ? Array.isArray(currentAnswer) && currentAnswer.includes(option.value) : currentAnswer === option.value;
          return <button key={option.value} onClick={() => {
            if (isMulti) {
              const current = Array.isArray(currentAnswer) ? currentAnswer : [];
              const newAnswer = isSelected ? current.filter(v => v !== option.value) : [...current, option.value];
              onAnswer(currentQuestion, newAnswer);
            } else {
              onAnswer(currentQuestion, option.value);
            }
          }} className={`w-full p-4 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-4 animate-slide-up ${isSelected ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-xl scale-105' : 'bg-white/80 backdrop-blur text-purple-900 hover:bg-white shadow-lg'}`} style={{
            animationDelay: `${idx * 50}ms`
          }} data-id="element-421">
                <span className="text-3xl" data-id="element-422">{option.emoji}</span>
                <span className="flex-1 text-left" data-id="element-423">{option.label}</span>
                {isMulti && isSelected && <span className="text-2xl" data-id="element-424">✓</span>}
              </button>;
        })}
        </div>

        {/* Multi-select continue */}
        {isMulti && hasAnswer && <button onClick={onMultiContinue} className="w-full bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold py-5 px-8 rounded-full shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 text-lg" data-id="element-425">
            Continue
            <ArrowRight className="w-5 h-5" data-id="element-426" />
          </button>}
      </div>
    </div>;
}
function LoadingSection() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(p => Math.min(p + 2, 100));
    }, 100);
    return () => clearInterval(timer);
  }, []);
  return <div className="min-h-screen flex items-center justify-center px-4" data-id="element-427">
      <div className="w-full max-w-md text-center" data-id="element-428">
        <div className="text-8xl mb-8 animate-spin-slow" data-id="element-429">✨</div>

        <h2 className="text-3xl font-bold text-purple-900 mb-4" data-id="element-430">
          Analyzing Your Results...
        </h2>

        <div className="bg-white/50 rounded-full h-4 mb-8 overflow-hidden" data-id="element-431">
          <div className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 transition-all duration-300 rounded-full" style={{
          width: `${progress}%`
        }} data-id="element-432" />
        </div>

        <p className="text-purple-600 animate-pulse" data-id="element-433">
          Finding your perfect belly type...
        </p>
      </div>
    </div>;
}
function ResultsSection({
  resultType,
  primaryGoal,
  onContinue
}: {
  resultType: BellyType;
  primaryGoal: PrimaryGoal;
  onContinue: () => void;
}) {
  const results = {
    TYPE1: {
      emoji: '😰',
      name: 'Stress Belly',
      color: 'from-red-400 to-orange-400',
      description: 'Your belly is a stress hormone issue!'
    },
    TYPE2: {
      emoji: '🔥',
      name: 'Inflammation Belly',
      color: 'from-orange-400 to-yellow-400',
      description: 'Your immune system needs support!'
    },
    TYPE3: {
      emoji: '💧',
      name: 'Water Retention Belly',
      color: 'from-blue-400 to-cyan-400',
      description: 'Your body is holding extra water!'
    },
    TYPE4: {
      emoji: '🧊',
      name: 'Frozen Metabolism Belly',
      color: 'from-purple-400 to-pink-400',
      description: 'Your metabolism needs a reset!'
    }
  };
  const result = results[resultType];
  return <div className="min-h-screen flex items-center justify-center px-4 py-8" data-id="element-434">
      <div className="w-full max-w-md text-center animate-fade-in" data-id="element-435">
        <div className="text-8xl mb-6 animate-bounce" data-id="element-436">{result.emoji}</div>

        <div className={`bg-gradient-to-r ${result.color} text-white rounded-3xl p-8 mb-6 shadow-2xl`} data-id="element-437">
          <h2 className="text-3xl font-bold mb-2" data-id="element-438">{result.name}</h2>
          <p className="text-lg" data-id="element-439">{result.description}</p>
        </div>

        <div className="bg-white/80 backdrop-blur rounded-3xl p-6 mb-6 shadow-xl" data-id="element-440">
          <p className="text-purple-900 font-medium mb-4" data-id="element-441">
            ✨ Your personalized plan is ready!
          </p>
          <p className="text-sm text-purple-600" data-id="element-442">
            5-minute morning ritual designed for YOUR body type
          </p>
        </div>

        <button onClick={onContinue} className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-6 px-8 rounded-full shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 text-xl" data-id="element-443">
          <Heart className="w-6 h-6" data-id="element-444" />
          See My Plan
          <ArrowRight className="w-6 h-6" data-id="element-445" />
        </button>
      </div>
    </div>;
}
function OfferSection() {
  const checkoutUrl = 'https://www.voicesoffreedomus.com/cart/45604499587258:1';
  return <div className="min-h-screen flex items-center justify-center px-4 py-8" data-id="element-446">
      <div className="w-full max-w-md text-center animate-fade-in" data-id="element-447">
        <div className="text-6xl mb-6 animate-bounce-slow" data-id="element-448">🌟</div>

        <h1 className="text-3xl font-bold text-purple-900 mb-4 leading-tight" data-id="element-449">
          Your Golden Root Ritual
        </h1>

        <p className="text-lg text-purple-700 mb-6" data-id="element-450">
          5-minute morning ritual that works WITH your hormones
        </p>

        <div className="bg-white/80 backdrop-blur rounded-3xl p-8 mb-6 shadow-xl" data-id="element-451">
          <div className="text-6xl font-bold text-purple-900 mb-2" data-id="element-452">$17</div>
          <p className="text-sm text-purple-600 line-through mb-4" data-id="element-453">Was $97</p>
          <p className="text-xs text-purple-500" data-id="element-454">Instant Digital Download</p>
        </div>

        <div className="space-y-3 mb-8" data-id="element-455">
          {[{
          emoji: '🍵',
          text: 'Golden Root Tonic Recipe'
        }, {
          emoji: '👐',
          text: '2-Min Lymphatic Flush'
        }, {
          emoji: '🧠',
          text: 'Cortisol Reset Protocol'
        }, {
          emoji: '🛒',
          text: '7-Day Grocery List'
        }].map((item, idx) => <div key={idx} className="bg-white/60 backdrop-blur rounded-2xl p-4 flex items-center gap-3 animate-slide-up" style={{
          animationDelay: `${idx * 100}ms`
        }} data-id="element-456">
              <span className="text-3xl" data-id="element-457">{item.emoji}</span>
              <span className="font-medium text-purple-900" data-id="element-458">{item.text}</span>
            </div>)}
        </div>

        <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-6 px-8 rounded-full shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 text-xl mb-4" data-id="element-459">
          <Zap className="w-6 h-6" data-id="element-460" />
          Get My Plan - $17
          <ArrowRight className="w-6 h-6" data-id="element-461" />
        </a>

        <p className="text-xs text-purple-500" data-id="element-462">🛡️ 7-Day Money-Back Guarantee</p>
      </div>
    </div>;
}